import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import ProductItem from "../ProductItem";

export default function InfiniteProducts(props) {
    const ServicesRef = useRef(null);
    const listRef = useRef(null);
    const {
        products = [],
        addItemToWishlist,
        onProductClick,
        filters = null,
        hasNextPage,
        isFetching,
        onLoadMore,
        plpStorageScope = null,
    } = props;

    const filterData = useMemo(() => (Array.isArray(products) ? products : []), [products]);

    // Responsive column calculation
    const [numColumns, setNumColumns] = useState(4);

    useEffect(() => {
        const updateColumns = () => {
            const width = window.innerWidth;
            if (width < 768) {
                setNumColumns(2);
            } else if (width < 1024) {
                setNumColumns(3);
            } else {
                setNumColumns(4);
            }
        };



        updateColumns();

        window.addEventListener('resize', updateColumns);



        return () => {
            window.removeEventListener('resize', updateColumns);

        };
    }, []);

    // Group products into rows
    const rows = useMemo(() => {
        const result = [];
        for (let i = 0; i < filterData.length; i += numColumns) {
            result.push(filterData.slice(i, i + numColumns));
        }
        return result;
    }, [filterData, numColumns]);

    const virtualizer = useWindowVirtualizer({
        count: rows.length,
        estimateSize: () => 500, // Estimated height of a row (card height + gap)
        overscan: 5,
    });

    const virtualItems = virtualizer.getVirtualItems();

    // Infinite scroll trigger
    useEffect(() => {
        const lastItem = virtualItems[virtualItems.length - 1];
        if (!lastItem) return;

        if (
            lastItem.index >= rows.length - 1 &&
            hasNextPage &&
            !isFetching &&
            onLoadMore
        ) {
            onLoadMore();
        }
    }, [virtualItems, rows.length, hasNextPage, isFetching, onLoadMore]);




    return (
        <div ref={listRef} style={{ position: 'relative', width: '100%' }}>
            <div
                style={{
                    height: `${virtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                }}
            >
                {virtualItems.map((virtualRow) => {
                    const rowProducts = rows[virtualRow.index];
                    const rowGapRem = 1;
                    const colBasis =
                        numColumns <= 1
                            ? '100%'
                            : `calc((100% - ${(numColumns - 1) * rowGapRem}rem) / ${numColumns})`;
                    return (
                        <div
                            key={virtualRow.key}
                            data-index={virtualRow.index}
                            ref={virtualizer.measureElement}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${virtualRow.start}px)`,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'stretch',
                                gap: `${rowGapRem}rem`,
                                paddingLeft: 'max(1rem, env(safe-area-inset-left, 0px))',
                                paddingRight: 'max(1rem, env(safe-area-inset-right, 0px))',
                                boxSizing: 'border-box'
                            }}
                        >
                            {rowProducts.map((dt) => {
                                const localId = localStorage.getItem("mylocalid");
                                const itemProps = dt?._id === localId ? { ref: ServicesRef } : {};
                                const productKey = dt?._id ?? dt?.externalId ?? dt?.id;
                                return (
                                    <div
                                        id={productKey ? `product-${productKey}` : undefined}
                                        key={productKey ?? JSON.stringify(dt)}
                                        style={{
                                            flex: `1 1 ${colBasis}`,
                                            minWidth: 0,
                                            maxWidth: colBasis,
                                        }}
                                    >
                                        <ProductItem
                                            item={dt}
                                            {...itemProps}
                                            addItemToWishlist={addItemToWishlist}
                                            products={filterData}
                                            onProductClick={onProductClick}
                                            filters={filters}
                                            plpStorageScope={plpStorageScope}
                                        />
                                    </div>
                                );
                            })}
                            {/* Fill empty spots in the last row to maintain alignment if using flex space-between/around, 
                                but with flex-start and fixed widths it should be fine. 
                                Adding empty divs just in case we want to ensure left alignment if we used space-between.
                                With the current width calc, it should be fine.
                            */}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
